import { apiFetch } from './client';

/**
 * Provisioning an SFTP credential (POST /sftp-users) is a platform-admin action.
 * The owner is exactly one of `userId` (internal Keycloak user) XOR `clientId`
 * (external client with no portal login). From the partner Clients tab we always
 * provision against a `clientId`; internal users are provisioned elsewhere.
 */
export interface ProvisionSftpUserPayload {
  username: string;
  publicKey: string;
  userId?: string;
  clientId?: string;
  homeDirectory?: string;
  /** Optional CIDR restricting where this credential may connect from. */
  allowedSourceCidr?: string;
}

export interface SftpUser {
  id: string;
  username: string;
  clientName: string;
  homeDirectory: string | null;
  userId: string | null;
  clientId: string | null;
  isActive: boolean;
  allowedSourceCidr: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SftpUserFilters {
  clientId?: string;
  userId?: string;
  isActive?: boolean;
  /** Free-text search across username / owner. */
  q?: string;
}

export interface UpdateSftpUserPayload {
  publicKey?: string;
  homeDirectory?: string | null;
  isActive?: boolean;
  allowedSourceCidr?: string | null;
}

const buildQuery = (params: Record<string, string | number | boolean | undefined>): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

export const sftpUsersApi = {
  list: (params: SftpUserFilters = {}): Promise<SftpUser[]> => {
    const qs = buildQuery(params as Record<string, string | number | boolean | undefined>);
    return apiFetch<SftpUser[]>(`/sftp-users${qs}`);
  },

  get: (id: string): Promise<SftpUser> => {
    return apiFetch<SftpUser>(`/sftp-users/${id}`);
  },

  provision: (data: ProvisionSftpUserPayload): Promise<SftpUser> => {
    return apiFetch<SftpUser>('/sftp-users', { method: 'POST', data });
  },

  update: (id: string, data: UpdateSftpUserPayload): Promise<SftpUser> => {
    return apiFetch<SftpUser>(`/sftp-users/${id}`, { method: 'PATCH', data });
  },

  deactivate: (id: string): Promise<SftpUser> => {
    return apiFetch<SftpUser>(`/sftp-users/${id}`, { method: 'DELETE' });
  },
};
